/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import * as Tooltip from '@radix-ui/react-tooltip';

/**
 * A label for a control that does not carry one.
 *
 * **Why a primitive and not `title`.** The native tooltip waits roughly a second,
 * cannot be styled, renders in the OS chrome rather than the page, and never
 * appears at all for somebody on a keyboard. On an icon-only button — which the
 * topbar now has three of — that is the difference between a control you can
 * learn and one you have to guess at.
 *
 * `aria-label` stays on the button regardless: this is the sighted person's
 * label, not the accessible name, and a tooltip that is the only place a control
 * is named is a control screen readers cannot describe.
 *
 * **Do not also leave a `title` on the child.** Both would fire, one over the
 * other, half a second apart.
 */
export function Tip({
  label,
  side = 'bottom',
  children,
}: {
  label: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  children: React.ReactNode;
}) {
  return (
    <Tooltip.Root>
      {/* `asChild`, so the trigger is the button itself. A wrapper would break
          the flex rows these controls sit in and hide the very rectangles
          `topbar-responsive` measures. */}
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side={side}
          sideOffset={8}
          // Above the dialogs, because a tooltip inside an open task modal is
          // still a tooltip. `motion-surface` is the same open/close motion
          // every other surface in the app uses.
          className="motion-surface z-[90] select-none rounded-lg bg-ink px-2.5 py-1.5 text-2xs font-semibold text-bg shadow-overlay"
        >
          {label}
          <Tooltip.Arrow className="fill-ink" width={11} height={5} />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

/**
 * One provider for the whole app, at the root.
 *
 * The delay is the decision here. Radix's default is 700ms, which on a row of
 * icon buttons means you have moved on before the first one answers. 250ms with
 * `skipDelayDuration` means the first tooltip takes a beat — long enough that
 * passing over a button does not flash one — and every neighbour you move to
 * afterwards is immediate.
 */
export function TipProvider({ children }: { children: React.ReactNode }) {
  return (
    <Tooltip.Provider delayDuration={250} skipDelayDuration={400}>
      {children}
    </Tooltip.Provider>
  );
}
